// Intent pages — authority cluster, part 2 (clinical topics).

export const CLINICAL_PAGES = {
  "metabolic-health": {
    slug: "metabolic-health",
    metaTitle: "Metabolic Health Testing & Optimization | Integrated Wellness",
    metaDescription:
      "What is metabolic health? Insulin sensitivity, CGM, mitochondrial and inflammatory markers — measured together and turned into physician-reviewed protocols at Integrated Wellness.",
    eyebrow: "Metabolic Health",
    h1: "Metabolic Health, Measured & Optimized",
    isMedical: true,
    image: "https://static.prod-images.emergentagent.com/jobs/c8a65975-d751-4172-9e88-294ec65cc742/images/df77be67313bdbdd64611eb5a89ce32b58f307d1ed6e95ef42ff6332f052f5b5.jpeg",
    imageAlt: "Connected wearable metrics supporting longitudinal metabolic health monitoring",
    about: "Metabolic Health",
    directAnswer:
      "Metabolic health describes how efficiently your body regulates blood sugar, insulin, lipids and energy production. Integrated Wellness measures it with fasting insulin, continuous glucose monitoring, mitochondrial and inflammatory markers — interpreted together by the Integrated Matrix Engine and turned into physician-reviewed nutrition, movement and treatment protocols.",
    sections: [
      {
        q: "What is metabolic health?",
        a: "Metabolic health is the capacity to process fuel efficiently — stable glucose, appropriate insulin response, healthy lipids and blood pressure, and a waistline in range. Studies suggest only a minority of adults meet all criteria, often without symptoms.",
        detail: null,
      },
      {
        q: "What is metabolic optimization?",
        a: "Metabolic optimization is the process of measuring your glucose regulation, insulin sensitivity, mitochondrial function and oxidative stress — then tuning nutrition timing, training zones, sleep and, where clinically appropriate, medication to improve those measurements over time.",
        detail:
          "It is the master lever for energy, body composition and long-term cardiometabolic risk — which is why it anchors many of our longevity protocols.",
      },
      {
        q: "What is insulin resistance — and why test for it early?",
        a: "Insulin resistance means your cells respond sluggishly to insulin, forcing your pancreas to produce more. It can develop for a decade before glucose looks abnormal — fasting insulin and post-meal glucose patterns are designed to surface it far earlier than a standard A1c alone.",
        detail: null,
      },
      {
        q: "How does continuous glucose monitoring (CGM) help?",
        a: "A CGM shows your real-world glucose response to your actual meals, workouts, stress and sleep — not a lab snapshot. Two weeks of CGM data typically reveals which specific foods and habits drive your glucose variability.",
        detail:
          "CGM data streams into your Digital Twin, where it is read alongside labs, training load and recovery — patterns that no single data source shows on its own.",
      },
      {
        q: "What does a metabolic protocol include?",
        a: "Personalized nutrition and meal-timing targets, glycemic strategy, exercise zone prescriptions, sleep and stress inputs, targeted supplementation, and physician-directed medication where indicated. Progress is verified by retesting — not by feel.",
        detail: null,
      },
    ],
    measures: [
      "Fasting Insulin & HOMA-IR",
      "CGM Glucose Patterns",
      "Mitochondrial Markers",
      "Oxidative Stress",
      "Lipid Fractionation",
      "Body Composition",
    ],
    faqs: [
      {
        q: "My A1c is normal. Do I still need this?",
        a: "A normal A1c can coexist with early insulin resistance. Fasting insulin, glucose variability and lipid patterns often shift years earlier — measuring them is how you stay ahead.",
      },
      {
        q: "Do I need medication to improve metabolic health?",
        a: "Often no. Nutrition, training, sleep and body-composition changes are first-line. Where medication is appropriate, it is prescribed and monitored by our physicians.",
      },
      {
        q: "What is metabolic age?",
        a: "Metabolic age compares your metabolic markers with population norms for each age. It is one of the outputs of our metabolic assessment and a motivating number to track over time.",
      },
      {
        q: "How long until measurable change?",
        a: "CGM patterns can shift within weeks; insulin sensitivity and lipid markers typically show measurable movement over 8–16 weeks of consistent protocol work.",
      },
    ],
    related: [
      { label: "Metabolic Optimization Program", href: "/programs/metabolic-optimization" },
      { label: "Insulin Resistance Testing", href: "/insulin-resistance-testing" },
      { label: "Intermittent Fasting", href: "/intermittent-fasting" },
      { label: "Metabolic Age", href: "/metabolic-age" },
      { label: "Continuous Glucose Monitoring", href: "/continuous-glucose-monitoring" },
    ],
  },

  "hormone-optimization": {
    slug: "hormone-optimization",
    metaTitle: "Hormone Optimization: Testing, Balance & Oversight | Integrated Wellness",
    metaDescription:
      "How does hormone optimization work? Full-axis testing for men and women — sex hormones, thyroid, cortisol — with physician-prescribed, titrated protocols at Integrated Wellness.",
    eyebrow: "Hormone Health",
    h1: "Hormone Optimization With Clinical Oversight",
    isMedical: true,
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/uihfi9t2_n8Oo7oN4SQM7DJDjAayH3%20%281%29.png",
    imageAlt: "Clinical team reviewing hormone balance dashboards — physician-supervised optimization",
    about: "Hormone Optimization",
    directAnswer:
      "Hormone optimization starts with mapping your full hormonal axes — sex hormones, thyroid, cortisol and their metabolic context — then restoring balance through physician-prescribed, carefully titrated protocols. At Integrated Wellness the philosophy is balance, never blast: comprehensive testing first, conservative dosing, and quarterly retesting with your physician.",
    sections: [
      {
        q: "What is hormone optimization?",
        a: "Hormone optimization is the clinical process of measuring your complete hormonal picture and correcting deficiencies or imbalances that affect energy, mood, libido, body composition, sleep and recovery — using lifestyle, nutrition and, where indicated, prescription therapy.",
        detail: null,
      },
      {
        q: "What do we test?",
        a: "Full sex-hormone panels (total and free testosterone, estradiol, progesterone, DHEA, SHBG), complete thyroid axis (TSH, free T3/T4, antibodies), cortisol rhythm, and the metabolic markers that determine how hormones actually behave in your body.",
        detail:
          "Isolated \u201clow T\u201d or \u201cthyroid\u201d tests miss the interactions. The Matrix Engine reads all axes together with your genetics and lifestyle data before your physician builds a plan.",
      },
      {
        q: "How does men's hormone health differ?",
        a: "Men most often present with declining testosterone, rising SHBG and metabolic drift — showing up as low energy, reduced drive, slower recovery and body-composition change. Protocols range from training, sleep and micronutrient correction to physician-prescribed therapy where clinically indicated.",
        detail: null,
      },
      {
        q: "How does women's hormone health differ?",
        a: "Women's hormones cycle and transition — perimenopause and menopause can alter estrogen, progesterone, thyroid and cortisol in overlapping ways that generic panels miss. Our protocols account for cycle phase and life stage, with therapy options reviewed by licensed clinicians.",
        detail: null,
      },
      {
        q: "How are protocols monitored?",
        a: "Quarterly lab retesting, symptom tracking, and wearable recovery trends — reviewed by your physician, who titrates dosing based on measured response. Every prescription decision stays with your clinician, supported by the engine's synthesis.",
        detail: null,
      },
    ],
    measures: [
      "Total & Free Testosterone",
      "Estradiol & Progesterone",
      "Full Thyroid Axis",
      "Cortisol Rhythm",
      "SHBG & DHEA",
      "Metabolic Context",
    ],
    faqs: [
      {
        q: "Is hormone therapy safe?",
        a: "When clinically indicated, physician-prescribed and properly monitored, hormone therapy has an established safety profile for appropriate candidates. Screening, conservative titration and scheduled retesting are how we manage risk.",
      },
      {
        q: "Do you treat both men and women?",
        a: "Yes. Protocols are built separately around male and female physiology, life stage and goals.",
      },
      {
        q: "Can I optimize hormones without prescriptions?",
        a: "Frequently, yes. Sleep, training, body composition and micronutrient status move hormone levels meaningfully. Prescription therapy is one option — not the default.",
      },
      {
        q: "Is this available via telehealth?",
        a: "Yes — consultations, lab orders and prescriptions are handled through nationwide telemedicine by Integrated Wellness Medical Center, with labs drawn locally.",
      },
    ],
    related: [
      { label: "Hormone Optimization Program", href: "/programs/hormone-optimization" },
      { label: "Peptide Therapy", href: "/peptide-therapy" },
      { label: "Men's Hormone Health", href: "/mens-hormone-health" },
      { label: "Women's Hormone Health", href: "/womens-hormone-health" },
      { label: "Testosterone Optimization", href: "/testosterone-optimization" },
    ],
  },

  "genetic-testing": {
    slug: "genetic-testing",
    metaTitle: "Genetic Testing: DNA, Pharmacogenomics & Methylation | Integrated Wellness",
    metaDescription:
      "How does DNA affect your health plan? Genetic testing, pharmacogenomics, MTHFR and methylation analysis — interpreted in clinical context at Integrated Wellness.",
    eyebrow: "Genetics",
    h1: "Genetic Testing, Interpreted in Context",
    isMedical: true,
    image: "https://static.prod-images.emergentagent.com/jobs/c8a65975-d751-4172-9e88-294ec65cc742/images/6db4e4022aa5f84e332c063eefb53789d57a2176579b7eb6dac5709701533aae.jpeg",
    imageAlt: "Clinical scientist reviewing genetic data in a precision laboratory",
    about: "Genetic Testing",
    directAnswer:
      "Genetic testing reads inherited variants that influence your disease predispositions, nutrient metabolism and medication response. Integrated Wellness analyzes DNA alongside your labs, microbiome and lifestyle data — because a genetic variant is a predisposition, not a destiny, and its real meaning depends on the biology you can measure today.",
    sections: [
      {
        q: "How does DNA affect medication response?",
        a: "Pharmacogenomics studies how genetic variants change the way you metabolize medications — some people clear a drug too fast to benefit, others too slowly and accumulate side effects. Testing key genes is designed to help your physician select agents and doses that fit your biology.",
        detail: null,
      },
      {
        q: "What is methylation testing — and what is MTHFR?",
        a: "Methylation is a biochemical process your body uses billions of times daily — for detoxification, neurotransmitters and DNA maintenance. Variants in genes like MTHFR can reduce methylation efficiency; testing shows whether your pathway needs targeted nutritional support.",
        detail:
          "MTHFR variants are common and frequently over-interpreted online. We read them in context — homocysteine, B-vitamin status and clinical picture — rather than treating a genotype as a diagnosis.",
      },
      {
        q: "What can genetic testing tell you about disease risk?",
        a: "Polygenic and single-gene analysis can identify elevated predispositions — cardiovascular, metabolic, nutrient-processing and more. Predisposition is probability, not fate: knowing yours is what lets prevention start decades early, where it works best.",
        detail: null,
      },
      {
        q: "How is DNA used inside your Digital Twin?",
        a: "Genetics is the fixed layer of your Digital Twin. The Matrix Engine reads your variants against your live data — labs, microbiome, wearables — and is designed to flag where an inherited tendency is actually expressing itself, so effort goes where your biology needs it.",
        detail: null,
      },
      {
        q: "Do you retest DNA?",
        a: "No — your genome doesn't change, so DNA is sequenced once. What changes is expression and downstream biology, which we track through labs, epigenetic (methylation-clock) testing and wearable trends.",
        detail: null,
      },
    ],
    measures: [
      "Pharmacogenomic Panel",
      "MTHFR & Methylation Genes",
      "Nutrient-Metabolism Variants",
      "Cardiovascular Risk Genes",
      "Detoxification Pathways",
      "Homocysteine Context",
    ],
    faqs: [
      {
        q: "Is this like a consumer ancestry test?",
        a: "No. Consumer tests are entertainment-grade reports. Clinical genetic analysis is physician-reviewed, focused on actionable variants, and interpreted against your labs and history.",
      },
      {
        q: "Is my genetic data private?",
        a: "Yes. Genetic data is encrypted, access-restricted to your care team, never sold and never shared without your explicit authorization.",
      },
      {
        q: "I already have raw DNA data. Can you use it?",
        a: "Often yes — existing raw data from quality sources can be re-analyzed clinically. Our team will confirm whether your file is usable during intake.",
      },
      {
        q: "Should everyone get genetic testing?",
        a: "It is most valuable when the results will change decisions — medication selection, prevention priorities, or targeted nutrition. Your clinician will tell you honestly whether it adds value for your case.",
      },
    ],
    related: [
      { label: "Precision Medicine", href: "/precision-medicine" },
      { label: "Pharmacogenomics", href: "/pharmacogenomics" },
      { label: "MTHFR Gene Testing", href: "/mthfr-gene-testing" },
      { label: "Digital Twin Platform", href: "/digital-twin" },
      { label: "Biological Age Testing", href: "/biological-age-testing" },
    ],
  },

  "microbiome-testing": {
    slug: "microbiome-testing",
    metaTitle: "Microbiome Testing & Gut Health | Integrated Wellness",
    metaDescription:
      "What does microbiome testing show? Gut composition, diversity, and gut-brain axis signals — sequenced and interpreted alongside labs and lifestyle data at Integrated Wellness.",
    eyebrow: "Microbiome",
    h1: "Microbiome Testing & Gut Health",
    isMedical: true,
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_integrated-matrix/artifacts/z6qvubun_8LaTqwkuF0-fKUUE60W-5.png",
    imageAlt: "Digestive system visualized with food and daily-habit tracking — gut health made measurable",
    about: "Gut Microbiome Health",
    directAnswer:
      "Microbiome testing sequences the trillions of bacteria in your gut to map composition, diversity and functional capacity. Because the gut influences digestion, immunity, metabolism and mood, Integrated Wellness reads your microbiome alongside labs, genetics and lifestyle data — turning a raw species list into a physician-reviewed gut-health plan.",
    sections: [
      {
        q: "What is microbiome testing?",
        a: "Microbiome testing analyzes a stool sample with DNA sequencing to identify which microbial species live in your gut, in what proportions, and what metabolic functions they perform — from fiber fermentation to neurotransmitter precursor production.",
        detail: null,
      },
      {
        q: "Why does gut health matter beyond digestion?",
        a: "Roughly 70% of immune tissue lines the gut, and gut microbes produce compounds that influence metabolism, inflammation and brain signaling. Imbalances are associated with issues far from the gut — energy, skin, mood and metabolic health among them.",
        detail: null,
      },
      {
        q: "What is the gut-brain axis?",
        a: "The gut-brain axis is the two-way communication network linking your gut and nervous system — via the vagus nerve, immune signaling and microbial metabolites like short-chain fatty acids. It is one reason stress shows up in digestion, and gut imbalance can show up in mood and focus.",
        detail: null,
      },
      {
        q: "What does a gut-health protocol include?",
        a: "Targeted dietary changes (fiber diversity, fermented foods, elimination where indicated), specific probiotic or prebiotic support matched to your sequencing results, lifestyle inputs like sleep and stress, and retesting to confirm the ecosystem actually shifted.",
        detail:
          "Generic probiotics often miss because they aren't matched to your ecosystem. Sequencing first means supporting the specific functions your gut is missing.",
      },
      {
        q: "How does microbiome data fit your Digital Twin?",
        a: "Gut signatures are read against your inflammatory labs, food-response patterns and recovery data. The Matrix Engine is designed to surface connections — like a low-diversity gut coinciding with elevated inflammatory markers — that justify targeted, rather than generic, intervention.",
        detail: null,
      },
    ],
    measures: [
      "Species Composition",
      "Diversity Index",
      "Short-Chain Fatty Acids",
      "Inflammatory Signatures",
      "Digestive Function Markers",
      "Gut-Brain Metabolites",
    ],
    faqs: [
      {
        q: "How is the sample collected?",
        a: "At home, with a simple mail-in kit. Results typically return in 2–4 weeks and are reviewed with our clinical team.",
      },
      {
        q: "Should I stop probiotics before testing?",
        a: "Usually yes — pausing probiotics for 1–2 weeks gives a truer picture of your resident ecosystem. We confirm specifics during intake.",
      },
      {
        q: "Can the microbiome really change?",
        a: "Yes — the microbiome is one of the most modifiable systems in your body. Meaningful composition shifts are measurable within weeks of consistent dietary change.",
      },
      {
        q: "Is microbiome testing diagnostic?",
        a: "No. It is an assessment tool that informs nutrition and lifestyle strategy and supports clinical evaluation — it does not by itself diagnose disease.",
      },
    ],
    related: [
      { label: "Gut-Brain Axis", href: "/gut-brain-axis" },
      { label: "Metabolic Health", href: "/metabolic-health" },
      { label: "Precision Medicine", href: "/precision-medicine" },
      { label: "Health Optimization Program", href: "/programs/health-optimization" },
    ],
  },
};
